import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Spinner,
} from "@chakra-ui/react";
import { setAwardDeclineSupplier } from "actions/RFQ/rfqsAction";
import { Button } from "newui/components/Button";
import { FileUpload } from "components/RFQ/FileUpload";
import { FieldForm } from "components/RFQ/Input";
import { Form, Formik } from "formik";
import React from "react";
import * as yup from "yup";
import { VStack } from "@chakra-ui/react";
import { CloseButton } from "components/RFQ/CloseButton";
import EmailPreview from "pages/EmailPreview/EmailPreview";
import { SubmissionSuccessfulEmailContent } from "components/email-template/SubmissionSuccessfulEmailContent";
import { useRfxMail } from "components/email-template/hooks/useRfxMail";
import { SubmissionUnsuccessfulEmailContent } from "components/email-template/SubmissionUnsuccessfulEmailContent";
import { isRfxOnboardingType } from "newui/features/Rfx/utils";
import { OnboardingSubmissionSuccessfulEmailContent } from "components/email-template/Onboarding/OnboardingSubmissionSuccessfulEmailContent";

const validationSchema = yup.object().shape({
	company: yup.string().required("Required"),
	rfq: yup.string().required("Required"),
	message: yup.string(),
});

export const AwardDeclineSupplierDrawer = ({
	isOpen,
	company,
	onClose,
	rfq,
	typeModal,
	setLoadRFQData,
	refreshData,
	guestName,
	guestEmail,
	isSubmissionPreview,
}) => {
	const [formValues, setFormValues] = React.useState(null);

	const companyId = typeModal?.companyId;
	const isAward = typeModal.type === "award";
	const supplierInvitedList = rfq?.supplierInvited?.all || [];
	const listExcludeAwards = [
		"unsuccessful",
		"decline",
		"awarded",
		"invited",
		"inprogress",
	];

	let filterInvitedList = supplierInvitedList.filter((f) => {
		return !listExcludeAwards.includes(f?.status?.toLowerCase());
	});

	const { rfxMail, additionalMessage, handleAdditionalMessageChange } =
		useRfxMail(rfq);

	const supplierOptions = company
		? [{ value: company?.id, label: company?.name }]
		: filterInvitedList?.map((company, index) => ({
				value: company?.company?.id
					? company?.company?.id
					: filterInvitedList[index]?.email,
				label: company?.company?.name
					? company?.company?.name
					: filterInvitedList[index]?.name,
			}));

	let initialValues = {
		rfq: `${rfq?.project?.name} ${rfq?.supplierList?.name ? `| ${rfq?.supplierList?.name}` : ""}`,
		company: guestEmail || parseInt(companyId) || "",
		message: "",
		status: isAward ? 1 : 0,
		file: null,
	};

	const handleAwardDeclineSubmit = async (values) => {
		!isSubmissionPreview && setLoadRFQData(true);

		values.email = guestEmail;

		let res = await setAwardDeclineSupplier(rfq?.id, values);

		if (res) {
			refreshData();
			onClose();
		}

		!isSubmissionPreview && setLoadRFQData(false);
	};

	const isOnboardingRFx = isRfxOnboardingType(rfq?.rfxType || rfq?.type);

	return (
		<>
			<Drawer
				placement={"right"}
				isOpen={isOpen}
				onClose={onClose}
				size="xl"
				className="bg-white p-11"
			>
				<DrawerOverlay onClick={onClose} />
				<DrawerContent maxWidth={"1280px"}>
					<DrawerHeader className="border-b-2">
						<Flex
							justifyContent="space-between"
							align={"center"}
						>
							<div>
								<p className="text-xl font-semibold text-bg-brand-solid">
									{isAward ? "Award Supplier" : "Decline Supplier"}
								</p>
								<p className="text-sm font-normal">
									Please notify the chosen supplier using the form below. You
									can preview the email that wil be sent, on the right
								</p>
							</div>
							<CloseButton onClose={onClose} />
						</Flex>
					</DrawerHeader>
					<DrawerBody>
						<div className="grid grid-cols-3">
							<div>
								<AwardDeclineForm
									initialValues={initialValues}
									validationSchema={validationSchema}
									handleRFQActionSubmit={handleAwardDeclineSubmit}
									selectionOptions={supplierOptions}
									guestName={guestName}
									onClose={onClose}
									setFormValues={setFormValues}
									companyId={companyId}
									handleAdditionalMessageChange={handleAdditionalMessageChange}
									additionalMessage={additionalMessage}
								/>
							</div>
							<div className="col-span-2">
								<p className="ml-10 px-11 text-lg font-semibold text-primary-900">
									{isAward
										? "Award Supplier Email Preview"
										: "Decline Supplier Email Preview"}
								</p>
								<EmailPreview>
									{isAward ? (
										isOnboardingRFx ? (
											<OnboardingSubmissionSuccessfulEmailContent
												rfxMail={rfxMail}
											/>
										) : (
											<SubmissionSuccessfulEmailContent rfxMail={rfxMail} />
										)
									) : (
										<SubmissionUnsuccessfulEmailContent rfxMail={rfxMail} />
									)}
								</EmailPreview>
							</div>
						</div>
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</>
	);
};

const AwardDeclineForm = ({
	initialValues,
	validationSchema,
	handleRFQActionSubmit,
	guestName,
	onClose,
	companyId,
	setFormValues,
	handleAdditionalMessageChange,
	additionalMessage,
	selectionOptions,
	hasFileUpload,
}) => {
	const handleAdditionalMessageOnChange = (e) => {
		handleAdditionalMessageChange(e.target.value);
	};

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={handleRFQActionSubmit}
			validationSchema={validationSchema}
			validateOnMount
			enableReinitialize
		>
			{({ isSubmitting, isValid, dirty, setFieldValue, values }) => {
				setFormValues(values);
				return (
					<Form>
						<VStack
							id="award-supplier-modal-body"
							spacing={6}
							align="stretch"
						>
							<FieldForm
								label="RFx"
								name="rfq"
								type="text"
								disabled={true}
								icon="file-attachment-04"
								className={"text-primary-500"}
							/>
							<FieldForm
								label="Company name"
								name="company"
								type="select"
								value={values.company}
								options={selectionOptions}
								isDisabled={companyId ? true : false}
							/>
							<FieldForm
								type="textArea"
								label="Message"
								name="message"
								placeholder="Enter a reason for this decision..."
								maxLength={500}
								onChange={handleAdditionalMessageOnChange}
								value={additionalMessage}
							/>
							{hasFileUpload && (
								<FileUpload
									type="file"
									name="file"
									className="my-4 w-full"
									types={["pdf", "docx", "xlsx", "jpeg", "jpg"]}
									defaultFileName="Document"
									isDisabled={true}
									maxFileSize={50}
								/>
							)}
							<Flex
								gap={3}
								className="!w-full"
							>
								<Button
									btntype={"base"}
									style={{ width: "100%" }}
									onClick={onClose}
									type="button"
								>
									Cancel
								</Button>
								<Button
									style={{ width: "100%" }}
									disabled={!isValid}
									type="submit"
								>
									{isSubmitting ? <Spinner></Spinner> : "Send"}
								</Button>
							</Flex>
						</VStack>
					</Form>
				);
			}}
		</Formik>
	);
};
